<template>
  <div>
    <b-form-checkbox 
      v-model="showDeletedLayers" 
      switch
    >
      Show deleted layers
    </b-form-checkbox>
    <b-list-group>
      <draggable
        id="layers-list"
        :list="layersOrdered"
        group="layers"
        @end="dragEnd"
        @start="dragStart"
      >
        <b-list-group-item
          v-for="field in layersOrdered"
          :key="field.id"
          :data-v-key="field.id"
          button
          :active="isActive(field)"
          :variant="listItemVariant(field)"
          :class="[
            (!field.relatedCanvasObject && !showDeletedLayers) ? 'd-none' : 'd-flex','justify-content-between align-items-center px-2 py-1',
            { 'font-weight-bold': !!field.relatedCanvasObject }
          ]"
          @click="(e) => setActiveObject(field.relatedCanvasObject, e)"
        >
          <div
            class="d-flex align-items-center"
            style="min-width: 40px; gap: 8px"
          >
            <b-icon
              :icon="getIcon(field.relatedCanvasObject)"
              @click.stop="handleLayerVisibility(field.relatedCanvasObject)"
            />
            <b-icon
              v-if="field.relatedCanvasObject?.isClippingMask"
              v-b-tooltip.hover.viewport.noninteractive="'Clipping mask'"
              icon="arrow90deg-down"
            />
          </div>

          <div class="flex-grow-1 text-center text-truncate px-2">
            <b-icon
              v-if="isOffScreen(field.relatedCanvasObject)"
              v-b-tooltip.hover.viewport.noninteractive="'This item is off-screen. Click to center it'"
              icon="bullseye"
              class="rounded-circle bg-primary mr-1"
              variant="light"
              @click="reposition(field.relatedCanvasObject)"
            />
            <b-tooltip
              v-if="!!field?.note"
              :target="`note-${field.id}`"
              triggers="hover"
            >
              {{ field.note }}
            </b-tooltip>
            <span
              :title="field.name"
              :disabled="isTooltipDisabled(field.name)"
            >
              {{ field.name }}
            </span>
            <b-icon
              v-if="!!field?.note"
              :id="`note-${field.id}`"
              icon="info-circle"
              class="ml-1"
            />
          </div>

          <div
            class="d-flex align-items-center"
            style="min-width: 24px"
          >
            <b-icon icon="grip-vertical" />
          </div>
        </b-list-group-item>
      </draggable>
    </b-list-group>
    <b-button-group size="sm">
      <b-button
        v-if="isMoreThanOneSelected"
        variant="outline-primary"
        size="sm"
        class="cursor-pointer mt-3"
        @click.stop="handleMultipleLayersVisibility"
      >
        <b-icon
          :icon="getSelectionVisibilityIcon"
          class="mr-1"
        />
        Toggle selection visibility
      </b-button>
    </b-button-group>
  </div>
</template>

<script>
/* global previews */
import {mapGetters} from 'vuex'
import draggable from 'vuedraggable'

import {editorControlEventBroker, EVENTS} from "@frontend/group/modules/editor-control-popup/event-broker";
import {
  ADDITIONAL_SAFE_ZONE_PREFIX_ELEMENT_ID,
  HORIZONTAL_GUIDE_LINE_PREFIX_ID,
  SAFE_ZONE_PREFIX_ID,
  SMART_OBJECT,
  VERTICAL_GUIDE_LINE_PREFIX_ID
} from "@frontend/constants/templates-field-prefixes";
import {FIELD_TYPES} from "@frontend/constants/field-types";
import {simpleTableBuilderEventBroker} from "@frontend/group/modules/simple-table-builder/event-broker";

const LAYER_VISIBILITY_ACTION = 'layerVisibility'

const NON_DRAGGABLE_PREFIXES = [
    SAFE_ZONE_PREFIX_ID,
    ADDITIONAL_SAFE_ZONE_PREFIX_ELEMENT_ID,
];

const BOTTOM_LIST_ITEMS = [SAFE_ZONE_PREFIX_ID, ADDITIONAL_SAFE_ZONE_PREFIX_ELEMENT_ID];

export default {
  name: 'LayersModule',

  components: {
    draggable,
  },

  props: {
    isTabActive: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    layersOrdered: [],
    selectionCount: 0,
    draggedIndex: null,
    hiddenLayers: [],
    hiddenTopLayers: [],
    hiddenBottomLayers: [],
    selectedLayers: [],
    clickedLayerIndex: null,
    showDeletedLayers: true 
  }),

  computed: {
    ...mapGetters('layout', [
      'layers',
      'target',
      'selectedInstance',
    ]),
    getIcon() {
      return object => object?.visible ? 'eye' : 'eye-slash';
    },
    isMoreThanOneSelected() {
      return this.selectionCount > 1;
    },
    getSelectionVisibilityIcon() {
      const activeObjects = this.selectedInstance?.canvas?.getActiveObjects();
      if (!activeObjects.length) return 'eye-slash';

      const isCurrentlyVisible = activeObjects.every(obj => obj.visible);
      return isCurrentlyVisible ? 'eye-slash' : 'eye';
    }
  },
  watch: {
    selectedInstance() {
      this.reorderLayers(this.selectedInstance.id);
      this.updateSelectionCount();
    },
    isTabActive() {
      this.rerenderList();
    },
    showDeletedLayers(val) {
      localStorage.setItem('showDeletedLayers', val);
    }
  },
  mounted() {
    const saved = localStorage.getItem('showDeletedLayers');
    if (saved !== null) {
      this.showDeletedLayers = saved === 'true';
    }
  },
  methods: {
    isActive (element) {
      // If group or activeSelection is selected
      const activeObjects = this.selectedInstance.canvas.getActiveObjects();
      const elementObj = element.relatedCanvasObject;
      const group = elementObj?.group;

      // 1. Object is directly selected
      if (activeObjects.includes(elementObj)) return true;

      // 2. Object is inside a selected group or activeSelection
      if (group && activeObjects.includes(group)) return true;
      
      if (element.element_id.startsWith(SMART_OBJECT)) {
        return (this.target?.type === FIELD_TYPES.SMART_OBJECT && (this.target.smartObjectId == element.element_id || this.target.smartObjectId == element.id)) || this.target?.type === FIELD_TYPES.NEW_SMART_OBJECT;
      } else {
        const canvas = this.selectedInstance?.canvas;
        const activeObjects = canvas?.getActiveObjects() || [];
        this.selectionCount = activeObjects.length;
        const targetId = this.target?.element_id || this.target?.id;
        const elementId = element.relatedCanvasObject?.element_id || element.relatedCanvasObject?.id;
        if (!elementId || !targetId) {
          return false;
        }
        return activeObjects.includes(element.relatedCanvasObject) || elementId === targetId;
      }
    },
    setActiveObject (object, e) {
      if (!object) {
        return
      }
      if (e && (e.ctrlKey || e.metaKey) && e.shiftKey ) {
        const currentIndex = this.layersOrdered.findIndex(layer => layer.relatedCanvasObject === object);
        const activeObjects = this.selectedInstance.canvas.getActiveObjects();
        const anchorObject = activeObjects[activeObjects.length - 1] || object;

        const anchorIndex = this.layersOrdered.findIndex(layer => layer.relatedCanvasObject === anchorObject);
        if (currentIndex === -1 || anchorIndex === -1) return;

        const [start, end] = currentIndex < anchorIndex ? [currentIndex, anchorIndex] : [anchorIndex, currentIndex];
        const rangeObjects = this.layersOrdered
          .slice(start, end + 1)
          .map(layer => layer.relatedCanvasObject)
          .filter(obj => obj);

        const newActiveObjects = activeObjects
          .filter(obj => {
            const index = this.layersOrdered.findIndex(layer => layer.relatedCanvasObject === obj);
            return index < start || index > end;
          })
          .concat(rangeObjects);

        this.selectedInstance.canvas.discardActiveObject();
        newActiveObjects.forEach(obj => obj.setCoords());

        const selection = new fabric.ActiveSelection(
          [...new Set(newActiveObjects)],
          { canvas: this.selectedInstance.canvas }
        );

        selection.setCoords();
        this.selectedInstance.canvas.setActiveObject(selection);
        this.selectedInstance.canvas.requestRenderAll();
        this.selectionCount = selection.getObjects().length;
        return;
      }

      if (e && (e.ctrlKey || e.metaKey)) {
        // getting active objects from canvas
        const activeObjects = this.selectedInstance.canvas.getActiveObjects();
        // checking if object is already selected, if so, remove it from selection
        let newActiveObjects = [...activeObjects];
        if (activeObjects.includes(object)) {
           newActiveObjects = activeObjects.filter(obj => obj !== object);
        } else {
          newActiveObjects = [...activeObjects, object];
        }
        this.selectedInstance.canvas.discardActiveObject();
        newActiveObjects.forEach(obj => {
          obj.setCoords()
        });

        const selection = new fabric.ActiveSelection(
          [...newActiveObjects],
          { canvas: this.selectedInstance.canvas }
        );

        selection.setCoords();
        this.selectedInstance.canvas.setActiveObject(selection);
        this.selectedInstance.canvas.requestRenderAll();
        this.selectionCount = newActiveObjects.length;
        return;
      } 
      
      if (object.element_id.startsWith(SMART_OBJECT)) {
        const preview = previews.find(preview => preview.id === this.selectedInstance.id)
        this.selectedInstance.canvas.fire('show:smart-object-editor', {instanceId: this.selectedInstance.id, preview: preview, smartObjectId: object.fieldId || object.element_id});
      } else {
        this.selectedInstance.canvas.setActiveObject(object)
        this.selectedInstance.canvas.renderAll() 
      }
      this.updateSelectionCount();
    },
    renderChanges () {
      [...this.layersOrdered]
          .reverse()
          .filter(layer => layer.relatedCanvasObject)
          .forEach(layer => {
            (layer.relatedCanvasObject.group || layer.relatedCanvasObject).bringToFront()
          })
      this.selectedInstance.canvas.renderAll()
      
      this.moveRulerGuideLinesToFront();
    },
    saveChanges () {
      const usersFieldsOrder = _.map(this.layersOrdered, 'id')
      this.selectedInstance.canvas.fire('layers:changed', {
        instance_id: this.selectedInstance.id,
        key: 'users_fields_order',
        value: usersFieldsOrder,
      })
    },
    reorderLayers(instanceId) {
      const usersFieldsOrder = JSON.parse(this.selectedInstance.data.users_fields_order || null);

      this.selectedInstance.canvas.fire('layers:update', instanceId);

      if (usersFieldsOrder) {
        this.layersOrdered = _.sortBy(this.layers, layer => usersFieldsOrder.findIndex(id => id === layer.id));
        
        this.moveRulerGuideLinesToFront();
        
        return;
      }

      this.layersOrdered = this.layers;
      this.moveRulerGuideLinesToFront();
    },
    isTooltipDisabled(name) {
        return name?.length < 25;
    },
    rerenderList() {
      if (this.isTabActive) {
        this.reorderLayers(this.selectedInstance.id);
      }
    },
    listItemVariant(field) {
      const isBottomItem = BOTTOM_LIST_ITEMS.some(prefix => field.element_id.startsWith(prefix));

      if (isBottomItem) {
        return 'dark';
      }

      return field.relatedCanvasObject ? '' : 'light'
    },
    handleLayerVisibility(object) {
      this.setActiveObject(object);
      
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: LAYER_VISIBILITY_ACTION,
        object
      });
    },
    isOffScreen (object) {
      if (object?.group?.type === 'activeSelection') {
        const selection = this.selectedInstance.canvas.getActiveObject();
        const rect = selection.getBoundingRect(true); 
        const objectRect = object.getBoundingRect(true);
        const objAbsCoords = {
          left: rect.left + rect.width/2 - objectRect.width/2 + object.left,
          top: rect.top + rect.height/2 - objectRect.height/2 + object.top,
          width: objectRect.width,
          height: objectRect.height
        }
        const canvas = this.selectedInstance.canvas;
        const canvasWidth = canvas.getWidth();
        const canvasHeight = canvas.getHeight();

        return objAbsCoords.left + objAbsCoords.width < 0 ||
          objAbsCoords.top + objAbsCoords.height < 0 ||
          objAbsCoords.left > canvasWidth ||
          objAbsCoords.top > canvasHeight;
      }
      return object?.selectable && !object.isOnScreen() && !isNaN(object.left + object.top)
    },
    reposition (object) {
      this.selectedInstance.canvas.centerObject(object)
      this.selectedInstance.canvas.fire('object:moving', { target: object })
      this.renderChanges()
    },
    moveRulerGuideLinesToFront() {
      this.selectedInstance.canvas
        .getObjects()
        .filter(
          obj => [HORIZONTAL_GUIDE_LINE_PREFIX_ID, VERTICAL_GUIDE_LINE_PREFIX_ID]
            .some(id => obj.id.startsWith(id))
        )
        .forEach(obj => obj.bringToFront());
    },
    updateSelectionCount() {
      this.selectionCount = this.selectedInstance.canvas.getActiveObjects().length;
    },
    handleMultipleLayersVisibility() {
      const activeObjects = this.selectedInstance.canvas.getActiveObjects();
      if (!activeObjects.length) return;

      const isCurrentlyVisible = activeObjects.every(obj => obj.visible);
      activeObjects.forEach(obj => {
        obj.visible = !isCurrentlyVisible;
        obj.setCoords();
      });

      this.selectedInstance.canvas.requestRenderAll();
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: LAYER_VISIBILITY_ACTION,
        object: activeObjects
      });
    },
    dragStart(e) {
      const listItems = [...document.querySelectorAll('#layers-list .list-group-item')];
      const selectedItems = listItems.filter(item => item.classList.contains('active'));
      const clickedItem = e.item;
      const clickedIndex = listItems.indexOf(clickedItem);
      // Store selectedLayers with original index, element, id, and layer
      this.selectedLayers = selectedItems.map(item => {
        const index = listItems.indexOf(item);
        let id = parseInt(item.getAttribute('data-v-key'));
        if (!id) {
          id = item.getAttribute('data-v-key');
        }
        const layer = this.layersOrdered.find(l => l.id === id);
        return {
          id,
          index,
          element: item,
          layer
        };
      });
      // Find which selected was clicked (by element)
      this.clickedLayerIndex = this.selectedLayers.findIndex(sel => sel.element === clickedItem);
      this.draggedIndex = clickedIndex;
      // Visually hide selected except clicked but only if we are dragging group of layers
      if (clickedItem.classList.contains('active')) {
        this.selectedLayers.forEach((sel, i) => {
          if (i === this.clickedLayerIndex) return;
          sel.element.style.transition = 'opacity 0.3s ease, height 0.3s ease, margin 0.3s ease, padding 0.3s ease';
          sel.element.style.opacity = '0';
          sel.element.style.height = '0';
          sel.element.style.margin = '0';
          sel.element.style.padding = '0';
          sel.element.classList.add('temporary-hidden');
        });
      }
    },
    dragEnd(e) {
      if (this.draggedIndex === null) return;
      // Use selectedLayers and clickedLayerIndex to separate top, clicked, bottom
      const selectedSorted = [...this.selectedLayers].sort((a, b) => a.index - b.index);
      const clicked = selectedSorted[this.clickedLayerIndex] || null;
      if (clicked === null || clicked === undefined) {
        this.renderChanges();
        this.saveChanges();
        return;
      }
      const top = selectedSorted.slice(0, this.clickedLayerIndex);
      const bottom = selectedSorted.slice(this.clickedLayerIndex + 1);
      const listItems = [...document.querySelectorAll('#layers-list .list-group-item')];

      const dropTarget = listItems.find(el =>
        el.classList.contains('active') && !el.classList.contains('temporary-hidden')
      );
      
      // filtered items
      const filteredItems = listItems.filter(el => !el.classList.contains('temporary-hidden'));

      const dropIndex = filteredItems.indexOf(dropTarget);
      // Remove all selected from layersOrdered
      const selectedIds = selectedSorted.map(s => s.id);
      this.layersOrdered = this.layersOrdered.filter(l => !selectedIds.includes(l.id));
      
     
      let insertIndex = dropIndex;
      insertIndex = Math.max(0, insertIndex);
      insertIndex = Math.min(this.layersOrdered.length, insertIndex);
      
      // Insert in preserved order
      this.layersOrdered.splice(
        insertIndex,
        0,
        ...top.map(x => x.layer),
        clicked.layer,
        ...bottom.map(x => x.layer)
      );
      // Restore visuals
      const hiddenItems = document.querySelectorAll('#layers-list .list-group-item.temporary-hidden');
      hiddenItems.forEach(item => {
        item.style.transition = '';
        item.style.opacity = '';
        item.style.height = '';
        item.style.margin = '';
        item.style.padding = '';
        item.classList.remove('temporary-hidden');
      });
      this.hiddenLayers = [];
      this.hiddenTopLayers = [];
      this.hiddenBottomLayers = [];
      this.draggedIndex = null;
      this.renderChanges();
      this.saveChanges();
    },
   
  }
}
</script>

<style scoped lang="scss">
.temporary-hidden {
  display: none !important;
  visibility: hidden;
}
</style>

